/**
 * Pinch zoom for the sheet scroller without hurting native scrolling:
 *  - WebKit (iPad/iPhone/macOS Safari): gesturestart/gesturechange/gestureend (preventDefault stops
 *    the browser's own page zoom). Touch events are never cancelled on WebKit.
 *  - Other browsers: a two-finger touch fallback (only two-finger moves are cancelled) and
 *    ctrl+wheel (trackpad pinch on Chrome/Edge/Firefox).
 * During the gesture the content is CSS-scaled around the focal point; at the end onCommit(k, ox, oy)
 * receives the scale factor and the focal point in content coordinates so the viewer can re-layout
 * at the new zoom and adjust scrollLeft/scrollTop (it also clears the transform).
 */
import { useEffect, useRef, type RefObject } from 'react';

interface WebKitGestureEvent extends Event {
  scale: number;
  clientX: number;
  clientY: number;
}

export interface PinchOptions {
  scrollerRef: RefObject<HTMLDivElement | null>;
  contentRef: RefObject<HTMLDivElement | null>;
  getZoom: () => number;
  minZoom: number;
  maxZoom: number;
  onCommit: (k: number, originX: number, originY: number) => void;
  /** Two-finger touch fallback for non-WebKit browsers (disable in edit mode). */
  touchFallback: boolean;
}

export const supportsGestureEvents = (): boolean => typeof window !== 'undefined' && 'ongesturestart' in window;

export function usePinchZoom(opts: PinchOptions): { isPinching: () => boolean } {
  const optsRef = useRef(opts);
  optsRef.current = opts;
  const state = useRef<{ ox: number; oy: number; k: number; z0: number; source: 'gesture' | 'touch' | 'wheel' } | null>(null);
  const raf = useRef(0);

  useEffect(() => {
    const el = opts.scrollerRef.current;
    if (!el) return;
    const webkit = supportsGestureEvents();

    const begin = (clientX: number, clientY: number, source: 'gesture' | 'touch' | 'wheel') => {
      const content = optsRef.current.contentRef.current;
      if (!content) return;
      const rect = el.getBoundingClientRect();
      const ox = el.scrollLeft + clientX - rect.left;
      const oy = el.scrollTop + clientY - rect.top;
      content.style.transformOrigin = `${ox}px ${oy}px`;
      content.style.willChange = 'transform';
      state.current = { ox, oy, k: 1, z0: optsRef.current.getZoom(), source };
    };

    const update = (scale: number) => {
      const s = state.current;
      if (!s || !Number.isFinite(scale) || scale <= 0) return;
      const { minZoom, maxZoom } = optsRef.current;
      s.k = Math.min(maxZoom / s.z0, Math.max(minZoom / s.z0, scale));
      if (raf.current) return;
      raf.current = requestAnimationFrame(() => {
        raf.current = 0;
        const content = optsRef.current.contentRef.current;
        const cur = state.current;
        if (!content || !cur) return;
        content.style.transform = `scale(${cur.k})`;
        // The jump pulse ring keeps its screen size (Pins.tsx); connector pins scale with the page.
        content.style.setProperty('--kgr-pin-scale', String(1 / cur.k));
      });
    };

    const end = () => {
      const s = state.current;
      if (!s) return;
      state.current = null;
      if (raf.current) {
        cancelAnimationFrame(raf.current);
        raf.current = 0;
      }
      const content = optsRef.current.contentRef.current;
      if (content) content.style.willChange = '';
      optsRef.current.onCommit(s.k, s.ox, s.oy);
    };

    // ── WebKit gesture events ──
    const onGestureStart = (e: Event) => {
      e.preventDefault();
      const g = e as WebKitGestureEvent;
      if (state.current) return;
      begin(g.clientX, g.clientY, 'gesture');
    };
    const onGestureChange = (e: Event) => {
      e.preventDefault();
      if (state.current?.source === 'gesture') update((e as WebKitGestureEvent).scale);
    };
    const onGestureEnd = (e: Event) => {
      e.preventDefault();
      if (state.current?.source === 'gesture') end();
    };

    // ── Two-finger touch fallback (non-WebKit only) ──
    let d0 = 0;
    const dist = (t: TouchList) => Math.hypot(t[0]!.clientX - t[1]!.clientX, t[0]!.clientY - t[1]!.clientY);
    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length !== 2 || state.current) return;
      d0 = dist(e.touches) || 1;
      begin((e.touches[0]!.clientX + e.touches[1]!.clientX) / 2, (e.touches[0]!.clientY + e.touches[1]!.clientY) / 2, 'touch');
    };
    const onTouchMove = (e: TouchEvent) => {
      if (state.current?.source !== 'touch' || e.touches.length !== 2) return;
      if (e.cancelable) e.preventDefault();
      update(dist(e.touches) / d0);
    };
    const onTouchEnd = (e: TouchEvent) => {
      if (state.current?.source === 'touch' && e.touches.length < 2) end();
    };

    // ── ctrl+wheel (desktop trackpad pinch / ctrl+scroll) ──
    let wheelK = 1;
    let wheelTimer: ReturnType<typeof setTimeout> | undefined;
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey) return;
      e.preventDefault();
      if (state.current && state.current.source !== 'wheel') return;
      if (!state.current) {
        wheelK = 1;
        begin(e.clientX, e.clientY, 'wheel');
      }
      const delta = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY;
      wheelK *= Math.exp(-delta * 0.01);
      update(wheelK);
      clearTimeout(wheelTimer);
      wheelTimer = setTimeout(end, 180);
    };

    const nonPassive: AddEventListenerOptions = { passive: false };
    if (webkit) {
      el.addEventListener('gesturestart', onGestureStart, nonPassive);
      el.addEventListener('gesturechange', onGestureChange, nonPassive);
      el.addEventListener('gestureend', onGestureEnd, nonPassive);
    } else if (opts.touchFallback) {
      el.addEventListener('touchstart', onTouchStart, { passive: true });
      el.addEventListener('touchmove', onTouchMove, nonPassive);
      el.addEventListener('touchend', onTouchEnd, { passive: true });
      el.addEventListener('touchcancel', onTouchEnd, { passive: true });
    }
    el.addEventListener('wheel', onWheel, nonPassive);
    return () => {
      clearTimeout(wheelTimer);
      el.removeEventListener('gesturestart', onGestureStart);
      el.removeEventListener('gesturechange', onGestureChange);
      el.removeEventListener('gestureend', onGestureEnd);
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchEnd);
      el.removeEventListener('wheel', onWheel);
      if (state.current) end();
    };
  }, [opts.scrollerRef, opts.touchFallback]);

  return { isPinching: () => state.current !== null };
}
